import axios from "axios";
import Button from "components/common/Button";
import { useState } from "react";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const initialFormState = {
  english: "",
  arabic: "",
  options: [
    { english: "", arabic: "", isCorrect: false },
    { english: "", arabic: "", isCorrect: false },
    { english: "", arabic: "", isCorrect: false }
  ],
  releaseDate: null,
  image: "",
};

const AddQuestion = () => {
  const [formState, setFormState] = useState(initialFormState);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [uploading, setUploading] = useState(false);

  const addOption = () => {
    setFormState((prev) => ({
      ...prev,
      options: [...prev.options, { english: "", arabic: "", isCorrect: false }],
    }));
  };

  const deleteOption = (idx) => {
    setFormState((prev) => {
      const newOptions = [...prev.options];

      newOptions.splice(idx, 1);

      return {
        ...prev,
        options: newOptions,
      };
    });
  };

  const updateIsCorrect = (e, idx) => {
    const { checked } = e.target;

    setFormState((prev) => {
      const newOptions = [...prev.options];

      newOptions[idx].isCorrect = checked;

      return { ...prev, options: newOptions };
    });
  };

  const updateOptionVal = (e, language, idx) => {
    const { value } = e.target;

    setFormState((prev) => {
      const newOptions = [...prev.options];

      newOptions[idx][language] = value;

      return { ...prev, options: newOptions };
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview("");
    setFormState((prev) => ({ ...prev, image: "" }));
  };

  const formSubmitHandler = async (e) => {
    e.preventDefault();
    setUploading(true);

    try {
      let imagePath = formState.image;

      // Upload image if selected
      if (imageFile) {
        const formData = new FormData();
        formData.append("image", imageFile);

        try {
          const uploadRes = await axios.post("/questions/upload-image", formData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          });
          imagePath = uploadRes.data.filePath;
        } catch (uploadErr) {
          console.log(uploadErr);
          toast.error(
            uploadErr?.response?.data?.message ||
              (uploadErr?.response?.data?.errors && uploadErr?.response?.data?.errors[0]) ||
              "Failed to upload image. Please try again."
          );
          setUploading(false);
          return;
        }
      }

      // Submit question with image path
      const formDataToSubmit = {
        ...formState,
        releaseDate: formState.releaseDate ? formState.releaseDate.toISOString().split('T')[0] : null,
        image: imagePath
      };
      await axios.post("/questions", formDataToSubmit);
      
      toast.success("Question added successfully!");
      // Reset form with completely new state
      setFormState({
        english: "",
        arabic: "",
        options: [
          { english: "", arabic: "", isCorrect: false },
          { english: "", arabic: "", isCorrect: false },
          { english: "", arabic: "", isCorrect: false }
        ],
        releaseDate: null,
        image: "",
      });
      setImageFile(null);
      setImagePreview("");
    } catch (err) {
      console.log(err);
      toast.error(
        err?.response?.data?.message ||
          (err?.response?.data?.errors && err?.response?.data?.errors[0]) ||
          "Something went wrong while adding the question"
      );
    } finally {
      setUploading(false);
    }
  };

  // Removed auto-date fetching - admins can now set any date

  return (
    <div className="py-20 sm:py-24 px-4 sm:px-6 md:px-10">
      <div className="max-w-2xl mx-auto">
        <Link to="/questions-list">
          <h5 className="text-xl sm:text-2xl md:text-3xl text-emerald-600 underline decoration-emerald-200 hover:decoration-emerald-500 text-center mb-5 sm:mb-6 font-semibold transition-colors">
            View questions list
          </h5>
        </Link>

        <form onSubmit={formSubmitHandler} className="bg-white/80 backdrop-blur-2xl border border-white/40 rounded-2xl p-5 sm:p-6 md:p-8 shadow-xl space-y-5">
          <div>
            <label className="text-base sm:text-lg font-medium text-slate-800" htmlFor="question-eng">
              Question statement (English)
            </label>
            <textarea
              className="mt-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 block w-full p-3 outline-none bg-white/60 text-base transition-all"
              type="text"
              value={formState.english}
              onChange={(e) =>
                setFormState((prev) => ({ ...prev, english: e.target.value }))
              }
            />
          </div>

          <div>
            <label className="text-base sm:text-lg font-medium text-slate-800" htmlFor="question-ara">
              Question statement (Arabic) - Optional
            </label>
            <textarea
              className="mt-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 block w-full p-3 outline-none text-right bg-white/60 text-base transition-all"
              type="text"
              value={formState.arabic}
              onChange={(e) =>
                setFormState((prev) => ({ ...prev, arabic: e.target.value }))
              }
              placeholder="اختياري"
            />
          </div>

          <div>
            <label className="text-base sm:text-lg font-medium text-slate-800" htmlFor="question-img">
              Question Image - Optional
            </label>
            <input
              className="mt-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 block w-full p-3 outline-none bg-white/60 text-sm sm:text-base transition-all file:mr-3 file:py-1 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-emerald-50 file:text-emerald-700 hover:file:bg-emerald-100"
              type="file"
              id="question-img"
              accept="image/*"
              onChange={handleImageChange}
            />
            {imagePreview && (
              <div className="relative mt-3">
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="max-w-full max-h-48 sm:max-h-64 rounded-xl"
                />
                <button
                  type="button"
                  onClick={removeImage}
                  className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center hover:bg-red-600 transition-colors shadow-md"
                >
                  &times;
                </button>
              </div>
            )}
          </div>

          <div>
            <label className="text-base sm:text-lg font-medium text-slate-800" htmlFor="question-date">
              Release Date
            </label>
            <DatePicker
              selected={formState.releaseDate}
              onChange={(date) => setFormState((prev) => ({ ...prev, releaseDate: date }))}
              minDate={new Date()}
              placeholderText="Select release date"
              dateFormat="yyyy-MM-dd"
              className="mt-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 block w-full p-3 outline-none bg-white/60 text-base transition-all"
              wrapperClassName="block w-full"
            />
          </div>

          <div className="border-t border-b border-slate-200 py-5">
            <h4 className="text-base sm:text-lg font-semibold text-slate-800 mb-4">Options</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-5">
              {formState.options.map((el, idx) => {
                return (
                  <div key={"question-option-" + idx} className="border-2 border-slate-200 rounded-xl p-4 bg-white/60 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex justify-between items-center mb-3">
                      <div className="font-semibold text-emerald-600 text-sm sm:text-base">Option {idx + 1}</div>
                      <img
                        className="cursor-pointer hover:scale-110 transition-transform w-5 h-5"
                        onClick={() => deleteOption(idx)}
                        src="/assets/imgs/bin.svg"
                        alt="bin"
                      />
                    </div>

                    <input
                      className="mb-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 block w-full p-2.5 outline-none bg-white/60 text-sm sm:text-base transition-all"
                      placeholder="English"
                      type="text"
                      value={el.english}
                      onChange={(e) => updateOptionVal(e, "english", idx)}
                    />
                    <input
                      className="mb-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 block w-full p-2.5 outline-none text-right bg-white/60 text-sm sm:text-base transition-all"
                      placeholder="Arabic (Optional - اختياري)"
                      type="text"
                      value={el.arabic}
                      onChange={(e) => updateOptionVal(e, "arabic", idx)}
                    />

                    <div className="inline-flex items-center mt-1">
                      <label htmlFor={el.english + idx} className="mr-3 text-sm text-slate-600">
                        Is Correct?
                      </label>
                      <span
                        id={el.english + idx}
                        className="relative flex items-center p-3 rounded-full cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          className="before:content[''] peer relative h-5 w-5 cursor-pointer appearance-none rounded-md border border-slate-300 transition-all before:absolute before:top-2/4 before:left-2/4 before:block before:h-12 before:w-12 before:-translate-y-2/4 before:-translate-x-2/4 before:rounded-full before:bg-blue-gray-500 before:opacity-0 before:transition-opacity checked:border-emerald-500 checked:bg-emerald-500 checked:before:bg-emerald-500 hover:before:opacity-10"
                          checked={el.isCorrect}
                          onChange={(e) => updateIsCorrect(e, idx)}
                        />
                        <span className="absolute text-white transition-opacity opacity-0 pointer-events-none top-2/4 left-2/4 -translate-y-2/4 -translate-x-2/4 peer-checked:opacity-100">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-3.5 w-3.5"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                            stroke="currentColor"
                            strokeWidth="1"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            ></path>
                          </svg>
                        </span>
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="pt-4 flex justify-center">
              <Button type="button" onClick={addOption}>
                Add Option
              </Button>
            </div>
          </div>

          <div className="pt-3 flex justify-center">
            <Button type="submit" disabled={uploading}>
              {uploading ? "Uploading..." : "Submit"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddQuestion;
